import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { consultantsService, CreateConsultantServiceDto } from '../../services/consultants.service';
import { Crown, AlertCircle, CheckCircle, Calendar as CalendarIcon, Plus, Trash2, Clock } from 'lucide-react';
import categoriesService, { Category, Subcategory } from '../../services/categories.service';
import fieldsService, { Field } from '../../services/fields.service';
import countriesService, { Country, Wilaya } from '../../services/countries.service';
import { authService } from '../../services/auth.service';
import creneauxService, { CreateCreneauData } from '../../services/creneaux.service';

const CreateServicePage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState<CreateConsultantServiceDto>({
    title: '',
    reference: '',
    country: '',
    city: '',
    wilaya_id: undefined,
    domaine_id: 0, // Will be validated to ensure it's not 0
    subcategory_id: '',
    format: 'presentiel',
    language: '',
    estimated_duration: '',
    full_description: '',
    skills_gained: '',
    learning_objectives: '',
    target_audience: '',
    consultant_names: '',
    banner_image: '',
    detailed_program_pdf: '',
  });
  const [durationValue, setDurationValue] = useState<number>(0);
  const [durationUnit, setDurationUnit] = useState<string>('heures');
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [canCreate, setCanCreate] = useState<{ canCreate: boolean; message?: string } | null>(null);
  const [checkingPermission, setCheckingPermission] = useState(true);
  
  // Hierarchical selection states
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [availableFields, setAvailableFields] = useState<Field[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);
  
  // Countries and wilayas states
  const [countries, setCountries] = useState<Country[]>([]);
  const [wilayas, setWilayas] = useState<Wilaya[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingWilayas, setLoadingWilayas] = useState(false);

  // Créneaux states
  const [creneaux, setCreneaux] = useState<CreateCreneauData[]>([]);
  const [showCreneauxSection, setShowCreneauxSection] = useState(false);

  useEffect(() => {
    checkCreatePermission();
    loadData();
  }, []);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (bannerPreview) {
        URL.revokeObjectURL(bannerPreview);
      }
    };
  }, [bannerPreview]);

  // Load subcategories based on consultant's category and countries
  const loadData = async () => {
    try {
      setLoadingSubcategories(true);
      setLoadingCountries(true);
      
      // Load countries and get consultant info in parallel
      const [countriesData, consultantInfo] = await Promise.all([
        countriesService.getCountries(),
        authService.getTrainingCenterInfo()
      ]);
      
      setCountries(countriesData);
      
      if (consultantInfo.category_id) {
        try {
          // Load subcategories for this consultant's category using categoriesService (same as admin)
          const subcategoriesData = await categoriesService.getSubcategoriesByCategory(consultantInfo.category_id);
          setSubcategories(subcategoriesData);
        } catch (subError) {
          // Try to load all subcategories as fallback
          try {
            const allSubcategories = await categoriesService.getSubcategories();
            setSubcategories(allSubcategories);
          } catch (fallbackError) {
          }
        }
      } else {
        // Try to load all subcategories as fallback
        try {
          const allSubcategories = await categoriesService.getSubcategories();
          setSubcategories(allSubcategories);
        } catch (fallbackError) {
        }
      }
    } catch (error) {
    } finally {
      setLoadingSubcategories(false);
      setLoadingCountries(false);
    }
  };

  // Load fields when subcategory changes
  useEffect(() => {
    const loadFields = async () => {
      if (formData.subcategory_id) {
        try {
          setLoadingFields(true);
          const fieldsData = await fieldsService.getFieldsBySubcategory(formData.subcategory_id);
          setAvailableFields(fieldsData);
        } catch (error) {
        } finally {
          setLoadingFields(false);
        }
      } else {
        setAvailableFields([]);
      }
    };

    loadFields();
  }, [formData.subcategory_id]);

  const checkCreatePermission = async () => {
    try {
      setCheckingPermission(true);
      // For now, allow creation - you can implement permission logic later
      setCanCreate({ canCreate: true });
    } catch (error) {
      setCanCreate({ canCreate: false, message: t('consultant.create.errors.permission', 'Error checking permissions') });
    } finally {
      setCheckingPermission(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Handle number inputs properly
    let processedValue: any = value;
    if (name === 'domaine_id') {
      // Convert to number, use 0 if empty or invalid
      const numValue = parseInt(value);
      processedValue = isNaN(numValue) ? 0 : numValue;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle country change and load wilayas if Algeria is selected
  const handleCountryChange = async (countryName: string) => {
    setFormData(prev => ({
      ...prev,
      country: countryName,
      city: '', 
      wilaya_id: undefined 
    }));

    if (countryName === 'Algeria') {
      try {
        setLoadingWilayas(true);
        const wilayasData = await countriesService.getWilayas();
        setWilayas(wilayasData);
      } catch (error) {
      } finally {
        setLoadingWilayas(false);
      }
    } else {
        setWilayas([]);
    }
  };

  const handleDurationChange = (value: number, unit: string) => {
    setDurationValue(value);
    setDurationUnit(unit);
    const durationString = value > 0 ? `${value} ${unit}` : '';
    setFormData(prev => ({
      ...prev,
      estimated_duration: durationString
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, fileType: 'banner' | 'pdf') => {
    const file = e.target.files?.[0];
    if (file) {
      if (fileType === 'banner') {
        // Clean up previous blob URL
        if (bannerPreview) {
          URL.revokeObjectURL(bannerPreview);
        }
        setBannerFile(file);
        // Create preview URL for display only
        const previewUrl = URL.createObjectURL(file);
        setBannerPreview(previewUrl);
        // Don't set blob URL in form data - let backend handle the file
      } else {
        setPdfFile(file);
        // PDF files don't need preview
      }
    }
  };

  // Créneaux management functions
  const addCreneau = () => {
    const newCreneau: CreateCreneauData = {
      service_id: 0, // Will be set after service creation
      creneau_date: '',
      start_time: '',
      end_time: '',
      is_available: true,
      notes: ''
    };
    setCreneaux([...creneaux, newCreneau]);
  };

  const removeCreneau = (index: number) => {
    setCreneaux(creneaux.filter((_, i) => i !== index));
  };

  const updateCreneau = (index: number, field: keyof CreateCreneauData, value: string | boolean) => {
    const updatedCreneaux = [...creneaux];
    updatedCreneaux[index] = { ...updatedCreneaux[index], [field]: value };
    setCreneaux(updatedCreneaux);
  };


  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est requis';
    }

    if (!formData.subcategory_id) {
      newErrors.subcategory_id = 'Veuillez sélectionner une sous-catégorie';
    }

    if (!formData.domaine_id || formData.domaine_id === null || formData.domaine_id === 0) {
      newErrors.domaine_id = 'Veuillez sélectionner un domaine';
    }

    if (!formData.full_description.trim()) {
      newErrors.full_description = 'La description complète est requise';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    
    try {
      // Create the service first
      const serviceData = {
        ...formData,
        estimated_duration: `${durationValue} ${durationUnit}`
      };

      const files = {
        banner: bannerFile || undefined,
        pdf: pdfFile || undefined,
      };

      const createdService = await consultantsService.createService(serviceData, files);
      
      // If there are créneaux, create them
      if (creneaux.length > 0) {
        const creneauxPromises = creneaux.map(creneau => 
          creneauxService.createCreneau({
            ...creneau,
            service_id: parseInt(createdService.id)
          })
        );
        
        await Promise.all(creneauxPromises);
      }
      
      // Show success message with approval notice
      const successMessage = 'Service créé avec succès !\n\nVotre service a été soumis pour approbation par l\'administrateur PHINLEX. Vous recevrez une notification une fois qu\'il sera approuvé ou rejeté.';
      alert(successMessage);
      navigate('/consultant/services');
    } catch (error) {
      // If backend enforces one free listing, it returns 403 on subsequent creations
      // Detect and guide user to subscription page
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const err: any = error;
      const status = err?.response?.status;
      const message = err?.response?.data?.message || 'Échec de la création du service. Veuillez réessayer.';
      if (status === 403) {
        const go = window.confirm(`${message}\n\nSouhaitez-vous consulter nos plans d'abonnement et téléverser un reçu ?`);
        if (go) {
          navigate('/consultant/subscriptions');
        }
      } else {
        alert(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading while checking permissions
  if (checkingPermission) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('consultant.create.loading.permissions', 'Checking permissions...')}</p>
        </div>
      </div>
    );
  }

  // Show subscription required message if can't create
  if (canCreate && !canCreate.canCreate) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{t('consultant.create.header.title', 'Create a Service')}</h1>
                <p className="text-gray-600">{t('consultant.create.header.subtitle', 'Add a new consulting service')}</p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => navigate('/consultant/services')}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Required Message */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6">
              <div className="flex items-center space-x-3">
                <Crown className="h-8 w-8 text-white" />
                <div>
                  <h2 className="text-2xl font-bold text-white">{t('consultant.create.subscription.title', 'Subscription Required')}</h2>
                  <p className="text-white/90">{t('consultant.create.subscription.reached', 'You have reached the limit of your current plan')}</p>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-yellow-100 rounded-full">
                  <AlertCircle className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('consultant.create.subscription.limitReached', 'Service limit reached')}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {canCreate.message || 'Vous avez atteint le nombre maximum de services autorisés avec votre plan actuel.'}
                  </p>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-blue-900">{t('subscriptions.freePlan.title', 'Free Plan')}</h4>
                        <p className="text-blue-700 text-sm">
                          {t('consultant.create.subscription.freeNote', 'You can publish 1 free service. To publish more, choose a subscription plan.')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-4">
                    <button
                      onClick={() => navigate('/consultant/subscriptions')}
                      className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:opacity-90 transition-colors flex items-center space-x-2"
                    >
                      <Crown className="h-5 w-5" />
                      <span>{t('consultant.create.subscription.viewPlans', 'View subscription plans')}</span>
                    </button>
                    <button
                      onClick={() => navigate('/consultant/services')}
                      className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      {t('consultant.create.subscription.backToServices', 'Back to Services')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('consultant.create.header.title', 'Create a Service')}</h1>
              <p className="text-gray-600">{t('consultant.create.header.subtitle', 'Add a new consulting service')}</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => navigate('/consultant/services')}
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                  {t('consultant.create.form.title', 'Service title *')}
                </label>
                <input
                  id="title"
                  name="title"
                  type="text"
                  required
                  value={formData.title}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm ${
                    errors.title ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
              </div>

              <div>
                <label htmlFor="reference" className="block text-sm font-medium text-gray-700">
                  {t('consultant.create.form.reference', 'Reference')}
                </label>
                <input
                  id="reference"
                  name="reference"
                  type="text"
                  value={formData.reference}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                />
              </div>
            </div>

            {/* Location */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                  {t('consultant.create.form.country', 'Country')}
                </label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={(e) => handleCountryChange(e.target.value)}
                  disabled={loadingCountries}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm ${
                    loadingCountries ? 'bg-gray-100' : 'border-gray-300'
                  }`}
                >
                  <option value="">
                    {loadingCountries ? t('consultant.create.form.countriesLoading', 'Loading countries...') : t('consultant.create.form.selectCountry', 'Select a country')}
                  </option>
                  {countries.map(country => (
                    <option key={country.id} value={country.name}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                  {formData.country === 'Algeria' ? t('consultant.create.form.wilaya', 'Wilaya') : t('consultant.create.form.city', 'City')}
                </label>
                {formData.country === 'Algeria' ? (
                  <select
                    id="city"
                    name="wilaya_id"
                    value={formData.wilaya_id || ''}
                    onChange={(e) => {
                      const wilayaId = parseInt(e.target.value);
                      setFormData(prev => ({
                        ...prev,
                        wilaya_id: wilayaId || undefined,
                        city: wilayas.find(w => w.id === wilayaId)?.name || ''
                      }));
                    }}
                    disabled={loadingWilayas}
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm ${
                      loadingWilayas ? 'bg-gray-100' : 'border-gray-300'
                    }`}
                  >
                    <option value="">
                      {loadingWilayas ? t('consultant.create.form.wilayasLoading', 'Loading wilayas...') : t('consultant.create.form.selectWilaya', 'Select a wilaya')}
                    </option>
                    {wilayas.map(wilaya => (
                      <option key={wilaya.id} value={wilaya.id}>
                        {wilaya.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id="city"
                    name="city"
                    type="text"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    placeholder={t('consultant.create.form.cityPlaceholder', 'Enter city name')}
                  />
                )}
              </div>
            </div>

            {/* Hierarchical Selection: Subcategory → Field */}
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">{t('consultant.create.form.expertiseCategory', 'Expertise category *')}</h3>
              
              {/* Subcategory Selection */}
              <div>
                <label htmlFor="subcategory_id" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('consultant.create.form.subcategory', 'Subcategory *')}
                </label>
                {loadingSubcategories ? (
                    <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                      <span className="ml-2 text-sm text-gray-500">{t('consultant.create.form.subcategoriesLoading', 'Loading subcategories...')}</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {subcategories.map((subcategory) => (
                      <label
                        key={subcategory.id}
                        className={`flex items-start p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          formData.subcategory_id === subcategory.id
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="subcategory"
                          value={subcategory.id}
                          checked={formData.subcategory_id === subcategory.id}
                          onChange={() => setFormData(prev => ({ ...prev, subcategory_id: subcategory.id, domaine_id: 0 }))}
                          className="sr-only"
                        />
                        <div className={`w-4 h-4 rounded-full border-2 mr-3 mt-0.5 flex items-center justify-center ${
                          formData.subcategory_id === subcategory.id
                            ? 'border-purple-500 bg-purple-500'
                            : 'border-gray-300'
                        }`}>
                          {formData.subcategory_id === subcategory.id && (
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-700">
                            {subcategory.name_french}
                          </div>
                          <div className="text-xs text-gray-500">
                            {subcategory.name_english}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
                {errors.subcategory_id && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {errors.subcategory_id}
                  </p>
                )}
              </div>

              {/* Field Selection - Only show if subcategory is selected */}
              {formData.subcategory_id && (
                <div>
                  <label htmlFor="domaine_id" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('consultant.create.form.domain', 'Domain *')}
                  </label>
                  {loadingFields ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                      <span className="ml-2 text-sm text-gray-500">{t('consultant.create.form.domainsLoading', 'Loading fields...')}</span>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {availableFields.map((field) => (
                        <label
                          key={field.id}
                          className={`flex items-start p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            formData.domaine_id === field.id
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="field"
                            value={field.id}
                            checked={formData.domaine_id === field.id}
                            onChange={() => setFormData(prev => ({ ...prev, domaine_id: field.id }))}
                            className="sr-only"
                          />
                          <div className={`w-4 h-4 rounded-full border-2 mr-3 mt-0.5 flex items-center justify-center ${
                            formData.domaine_id === field.id
                              ? 'border-purple-500 bg-purple-500'
                              : 'border-gray-300'
                          }`}>
                            {formData.domaine_id === field.id && (
                              <div className="w-2 h-2 bg-white rounded-full"></div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-700">
                              {field.name_french}
                            </div>
                            <div className="text-xs text-gray-500">
                              {field.name_english}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                  {errors.domaine_id && (
                    <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {errors.domaine_id}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Service Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="format" className="block text-sm font-medium text-gray-700">
                  {t('consultant.create.form.format', 'Format')}
                </label>
                <select
                  id="format"
                  name="format"
                  value={formData.format}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                >
                  <option value="presentiel">{t('consultant.create.form.format.presentiel', 'In-person')}</option>
                  <option value="distance">{t('consultant.create.form.format.distance', 'Remote')}</option>
                  <option value="hybride">{t('consultant.create.form.format.hybride', 'Hybrid')}</option>
                </select>
              </div>

              <div>
                <label htmlFor="language" className="block text-sm font-medium text-gray-700">
                  {t('consultant.create.form.languages', 'Languages')}
                </label>
                <select
                  id="language"
                  name="language"
                  value={formData.language}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                >
                  <option value="">{t('consultant.create.form.selectLanguage', 'Select language')}</option>
                  <option value="Arabe">{t('consultant.create.form.lang.arabic', 'Arabic')}</option>
                  <option value="Anglais">{t('consultant.create.form.lang.english', 'English')}</option>
                  <option value="Français">{t('consultant.create.form.lang.french', 'French')}</option>
                  <option value="Arabe/Anglais">{t('consultant.create.form.lang.arabicEnglish', 'Arabic/English')}</option>
                </select>
              </div>

              <div>
                <label htmlFor="estimated_duration" className="block text-sm font-medium text-gray-700">
                  {t('consultant.create.form.estimatedDuration', 'Estimated duration')}
                </label>
                <div className="mt-1 flex gap-2">
                  <input
                    id="estimated_duration"
                    name="estimated_duration"
                    type="number"
                    min="0"
                    value={durationValue}
                    onChange={(e) => handleDurationChange(Number(e.target.value), durationUnit)}
                    placeholder="0"
                    className="block w-24 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  />
                  <select
                    value={durationUnit}
                    onChange={(e) => handleDurationChange(durationValue, e.target.value)}
                    className="block px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  >
                    <option value="heures">{t('consultant.create.form.duration.hours', 'Hours')}</option>
                    <option value="jours">{t('consultant.create.form.duration.days', 'Days')}</option>
                    <option value="semaines">{t('consultant.create.form.duration.weeks', 'Weeks')}</option>
                    <option value="mois">{t('consultant.create.form.duration.months', 'Months')}</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="full_description" className="block text-sm font-medium text-gray-700">
                {t('consultant.create.form.fullDescription', 'Full description *')}
              </label>
              <textarea
                id="full_description"
                name="full_description"
                rows={4}
                value={formData.full_description}
                onChange={handleInputChange}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm ${
                  errors.full_description ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder={t('consultant.create.form.fullDescriptionPlaceholder', 'Detailed service description...')}
              />
              {errors.full_description && <p className="mt-1 text-sm text-red-600">{errors.full_description}</p>}
            </div>

            {/* Objectives and Skills */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="learning_objectives" className="block text-sm font-medium text-gray-700">
                  {t('consultant.create.form.objectives', 'Objectives')}
                </label>
                <textarea
                  id="learning_objectives"
                  name="learning_objectives"
                  rows={3}
                  value={formData.learning_objectives}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  placeholder={t('consultant.create.form.objectivesPlaceholder', 'Service objectives...')}
                />
              </div>

              <div>
                <label htmlFor="skills_gained" className="block text-sm font-medium text-gray-700">
                  {t('consultant.create.form.skills', 'Skills applied / Areas covered')}
                </label>
                <textarea
                  id="skills_gained"
                  name="skills_gained"
                  rows={3}
                  value={formData.skills_gained}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  placeholder={t('consultant.create.form.skillsPlaceholder', 'Skills you will develop...')}
                />
              </div>
            </div>

            {/* Target Companies and Consultant Names */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="target_audience" className="block text-sm font-medium text-gray-700">
                  {t('consultant.create.form.targetCompanies', 'Target companies')}
                </label>
                <textarea
                  id="target_audience"
                  name="target_audience"
                  rows={3}
                  value={formData.target_audience}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  placeholder={t('consultant.create.form.targetCompaniesPlaceholder', 'Types of companies that would benefit from this service...')}
                />
              </div>

              <div>
                <label htmlFor="consultant_names" className="block text-sm font-medium text-gray-700">
                  {t('consultant.create.form.consultantNames', 'Consultants names')}
                </label>
                <textarea
                  id="consultant_names"
                  name="consultant_names"
                  rows={3}
                  value={formData.consultant_names}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  placeholder={t('consultant.create.form.consultantNamesPlaceholder', 'Consultants names and qualifications...')}
                />
              </div>
            </div>


            {/* File Uploads */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="banner_file" className="block text-sm font-medium text-gray-700">
                  {t('consultant.create.form.bannerImage', 'Banner image')}
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="banner_file"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-purple-600 hover:text-purple-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-purple-500"
                      >
                        <span>{t('consultant.create.form.uploadFile', 'Upload a file')}</span>
                        <input
                          id="banner_file"
                          name="banner_file"
                          type="file"
                          accept="image/*"
                          className="sr-only"
                          onChange={(e) => handleFileChange(e, 'banner')}
                        />
                      </label>
                      <p className="pl-1">{t('consultant.create.form.orDragDrop', 'or drag and drop')}</p>
                    </div>
                    <p className="text-xs text-gray-500">{t('consultant.create.form.bannerFormats', 'PNG, JPG up to 10MB')}</p>
                    {bannerFile && (
                      <p className="text-sm text-green-600">{t('consultant.create.form.selected', 'Selected')}: {bannerFile.name}</p>
                    )}
                    {bannerPreview && (
                      <div className="mt-2">
                        <img 
                          src={bannerPreview} 
                          alt="Banner preview" 
                          className="w-32 h-20 object-cover rounded border"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="pdf_file" className="block text-sm font-medium text-gray-700">
                  {t('consultant.create.form.pdfPresentation', 'PDF presentation')}
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="pdf_file"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-purple-600 hover:text-purple-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-purple-500"
                      >
                        <span>{t('consultant.create.form.uploadFile', 'Upload a file')}</span>
                        <input
                          id="pdf_file"
                          name="pdf_file"
                          type="file"
                          accept=".pdf"
                          className="sr-only"
                          onChange={(e) => handleFileChange(e, 'pdf')}
                        />
                      </label>
                      <p className="pl-1">{t('consultant.create.form.orDragDrop', 'or drag and drop')}</p>
                    </div>
                    <p className="text-xs text-gray-500">{t('consultant.create.form.pdfFormats', 'PDF up to 10MB')}</p>
                    {pdfFile && (
                      <p className="text-sm text-green-600">Sélectionné: {pdfFile.name}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Créneaux Section */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <CalendarIcon className="h-6 w-6 text-purple-600" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{t('consultant.create.creneaux.title', 'One-to-one availabilities')}</h3>
                    <p className="text-sm text-gray-600">{t('consultant.create.creneaux.subtitle', 'Define your availability time slots for this service')}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowCreneauxSection(!showCreneauxSection)}
                  className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                >
                  {showCreneauxSection ? t('consultant.create.creneaux.hide', 'Hide') : t('consultant.create.creneaux.show', 'Show')} {t('consultant.create.creneaux.slots', 'slots')}
                </button>
              </div>

              {showCreneauxSection && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600">
                      Ajoutez vos créneaux de disponibilité. Les clients pourront réserver ces créneaux.
                    </p>
                    <button
                      type="button"
                      onClick={addCreneau}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Ajouter un créneau
                    </button>
                  </div>

                  {creneaux.length === 0 ? (
                    <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                      <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">{t('consultant.create.creneaux.none', 'No slots defined')}</h3>
                      <p className="mt-1 text-sm text-gray-500">{t('consultant.create.creneaux.empty', 'Start by adding your availability slots.')}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {creneaux.map((creneau, index) => (
                        <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-medium text-gray-900">
                              {t('consultant.create.creneaux.slotLabel', 'Slot')} {index + 1}
                            </h4>
                            <button
                              type="button"
                              onClick={() => removeCreneau(index)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Date */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                {t('consultant.create.creneaux.date', 'Date')}
                              </label>
                              <input
                                type="date"
                                value={creneau.creneau_date}
                                onChange={(e) => updateCreneau(index, 'creneau_date', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                                min={new Date().toISOString().split('T')[0]}
                              />
                            </div>

                            {/* Start Time */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                {t('consultant.create.creneaux.startTime', 'Start time')}
                              </label>
                              <input
                                type="time"
                                value={creneau.start_time}
                                onChange={(e) => updateCreneau(index, 'start_time', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                              />
                            </div>

                            {/* End Time */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                {t('consultant.create.creneaux.endTime', 'End time')}
                              </label>
                              <input
                                type="time"
                                value={creneau.end_time}
                                onChange={(e) => updateCreneau(index, 'end_time', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                              />
                            </div>
                          </div>

                          {/* Notes */}
                          <div className="mt-3">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              {t('consultant.create.creneaux.notes', 'Notes (optional)')}
                            </label>
                            <textarea
                              value={creneau.notes || ''}
                              onChange={(e) => updateCreneau(index, 'notes', e.target.value)}
                              rows={2}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                              placeholder={t('consultant.create.creneaux.notesPlaceholder', 'Add notes about this slot...')}
                            />
                          </div>

                          {/* Availability Toggle */}
                          <div className="mt-3 flex items-center">
                            <input
                              type="checkbox"
                              id={`available-${index}`}
                              checked={creneau.is_available}
                              onChange={(e) => updateCreneau(index, 'is_available', e.target.checked)}
                              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                            />
                            <label htmlFor={`available-${index}`} className="ml-2 text-sm text-gray-700">
                              {t('consultant.create.creneaux.available', 'Slot available')}
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <CheckCircle className="h-5 w-5 text-blue-400" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">
                          {t('consultant.create.creneaux.infoTitle', 'Slots information')}
                        </h3>
                        <div className="mt-2 text-sm text-blue-700">
                          <p>
                            {t('consultant.create.creneaux.infoText', 'The slots you define here will be available for clients to book. You can edit or add more slots after creating the service.')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Error Display */}
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      {t('consultant.create.errors.submitTitle', 'Error creating service')}
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{errors.submit}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end space-x-3 pt-6">
              <button
                type="button"
                onClick={() => navigate('/consultant/services')}
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 disabled:opacity-50"
              >
                {isSubmitting ? t('consultant.create.actions.creating', 'Creating...') : t('consultant.create.actions.create', 'Create service')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateServicePage;